import { useCallback } from "react";
import { useVideo } from "../context/VideoContext";

const useVideoMetadata = () => {
  const { videoMetadata, setVideoMetadata } = useVideo();

  // تابع استخراج اطلاعات از نام فایل
  const extractInfoFromFileName = useCallback((filename) => {
    const nameWithoutExt = filename.replace(/\.[^.]+$/, ""); // حذف پسوند

    // تشخیص سریال با الگوهای مختلف
    const seriesPatterns = [
      // الگوهای مختلف برای فصل و قسمت
      /(.*?)[.\-_ ]?[sS](\d{1,2})[.\-_ ]?[eE](\d{1,2})/i, // S01E01, s1e1, S01-E01
      /(.*?)[.\-_ ]?(\d{1,2})x(\d{1,2})/i, // 1x01, 01x01
      /(.*?)[.\-_ ]?[sS]eason[.\-_ ]?(\d{1,2})[.\-_ ]?[eE]pisode[.\-_ ]?(\d{1,2})/i, // Season 1 Episode 1
      /(.*?)[.\-_ ]?(\d{1,2})[.\-_ ]?of[.\-_ ]?\d+/i, // 1 of 10, 01 of 12
    ];

    // بررسی الگوهای سریال
    for (const pattern of seriesPatterns) {
      const match = nameWithoutExt.match(pattern);
      if (match) {
        let seriesName = match[1].replace(/[.\-_]/g, " ").trim();
        const season = parseInt(match[2], 10);
        let episode = parseInt(match[3] || match[2], 10); // برای بعضی الگوها

        // پاکسازی نام سریال از کلمات اضافی
        seriesName = cleanSeriesName(seriesName);

        return {
          videoType: "series",
          filmName: seriesName,
          fileName: filename,
          season: season.toString(),
          episode: episode.toString(),
        };
      }
    }

    // اگر سریال نبود، به عنوان فیلم پردازش کن
    const filmName = extractMovieName(nameWithoutExt);

    return {
      videoType: "movie",
      filmName: filmName,
      fileName: filename,
      season: "",
      episode: "",
    };
  }, []);

  // تابع کمکی برای پاکسازی نام سریال
  const cleanSeriesName = (name) => {
    // حذف کلمات اضافی که ممکن است در نام سریال باقی بمانند
    const unwantedWords = [
      "1080p",
      "720p",
      "480p",
      "2160p",
      "4k",
      "web",
      "webrip",
      "web-dl",
      "bluray",
      "brrip",
      "hdrip",
      "dvdrip",
      "x264",
      "x265",
      "hevc",
      "10bit",
      "aac",
      "dts",
      "6ch",
      "5.1",
      "2ch",
      "part",
      "pahe",
      "psa",
      "joy",
      "www",
      "com",
      "mkv",
      "mp4",
      "avi",
      "m2tv",
      "phd",
      "team",
    ];

    const words = name.split(/\s+/);
    const cleanWords = words.filter(
      (word) =>
        !unwantedWords.includes(word.toLowerCase()) && !/^\d{4}$/.test(word), // حذف سال‌ها
    );

    return cleanWords.join(" ").trim();
  };

  // تابع کمکی برای استخراج نام فیلم
  const extractMovieName = (nameWithoutExt) => {
    // کلمات کلیدی که نشان‌دهنده پایان نام فیلم هستند
    const keywords = [
      "1080p",
      "720p",
      "480p",
      "2160p",
      "4k",
      "web",
      "webrip",
      "web-dl",
      "bluray",
      "brrip",
      "hdrip",
      "dvdrip",
      "x264",
      "x265",
      "hevc",
      "10bit",
      "aac",
      "dts",
      "6ch",
      "5.1",
      "2ch",
      "part",
      "pahe",
      "psa",
      "joy",
      "www",
    ];

    const parts = nameWithoutExt.replace(/[._\-]/g, " ").split(/\s+/);
    const titleParts = [];

    for (const part of parts) {
      if (keywords.includes(part.toLowerCase()) || /^\d{4}$/.test(part)) {
        break;
      }
      titleParts.push(part);
    }

    return titleParts.join(" ").trim();
  };

  // تابع به‌روزرسانی metadata از پاسخ سرور
  const updateMetadataFromBackend = useCallback(
    (backendResponse, fileName) => {
      if (backendResponse?.data?.docs?.[0]) {
        const subtitleInfo = backendResponse.data.docs[0];

        // تعیین نوع محتوا
        let videoType = "movie"; // مقدار پیش‌فرض

        // بررسی انواع مختلفی که ممکنه از بک‌اند بیاد
        if (
          subtitleInfo.type === "serial" ||
          subtitleInfo.kind === "serial" ||
          subtitleInfo.serial === true ||
          subtitleInfo.season ||
          subtitleInfo.episode
        ) {
          videoType = "series";
        }

        // استخراج نام فیلم یا سریال
        let filmName =
          subtitleInfo.filmName ||
          subtitleInfo.seriesName ||
          subtitleInfo.title ||
          subtitleInfo.name ||
          "";

        // اگه filmName خالی بود، از fileName استخراج کن
        if (!filmName && fileName) {
          const extracted = extractInfoFromFileName(fileName);
          filmName = extracted.filmName;
          // اگه از فایل استخراج کردیم و سریال بود، نوع رو هم تنظیم کن
          if (!videoType || videoType === "movie") {
            videoType = extracted.videoType;
          }
        }

        // پاکسازی نام فیلم از اطلاعات اضافی (مثل کیفیت و ...)
        filmName = cleanSeriesName(filmName);

        const metadata = {
          videoType: videoType, // حالا "series" یا "movie"
          filmName: filmName,
          fileName: fileName,
          season: subtitleInfo.season?.toString() || "",
          episode: subtitleInfo.episode?.toString() || "",
          // نگهداری اطلاعات اضافی
          _source: "backend",
          _id: subtitleInfo._id,
        };

        setVideoMetadata(metadata);
      }
    },
    [setVideoMetadata, extractInfoFromFileName, cleanSeriesName], // cleanSeriesName رو هم به وابستگی‌ها اضافه کنید
  );

  // تابع تنظیم مستقیم metadata
  const setMetadata = useCallback(
    (metadata) => {
      setVideoMetadata(metadata);
    },
    [setVideoMetadata],
  );

  // تابع ریست metadata
  const resetMetadata = useCallback(() => {
    setVideoMetadata({
      videoType: "",
      filmName: "",
      fileName: "",
      season: "",
      episode: "",
    });
  }, [setVideoMetadata]);

  return {
    videoMetadata,
    extractInfoFromFileName,
    updateMetadataFromBackend,
    setMetadata,
    resetMetadata,
  };
};

export default useVideoMetadata;
